import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiMoreVertical,
  FiCornerUpLeft,
  FiEdit2,
  FiTrash2,
  FiFlag,
  FiSmile,
} from "react-icons/fi";
import DeleteConfirmModal from "./DeleteConfirmModal";

const MessageMenu = ({
  message,
  isMyMessage,
  onReply,
  onEdit,
  onDelete,
  onReport,
  onReact,
  forceShowMenu = false,
  onMenuClose,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const dropdownRef = useRef(null);

  const quickEmojis = ["❤️", "👍", "😂", "😮", "😢", "🔥"];

  // Handle force show menu from long press gesture
  useEffect(() => {
    if (forceShowMenu && !showMenu) {
      const button = menuRef.current?.querySelector(".msgx-menu-btn");
      if (button) {
        const buttonRect = button.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const menuHeight = 200;

        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        let top, left;

        if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
          top = buttonRect.top - menuHeight;
        } else {
          top = buttonRect.bottom + 5;
        }

        if (isMyMessage) {
          left = buttonRect.left;
        } else {
          left = buttonRect.right - 150;
        }

        setMenuPosition({ top, left });
        setShowMenu(true);
      }
    } else if (!forceShowMenu && showMenu) {
      setShowMenu(false);
    }
  }, [forceShowMenu]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
        setShowReactions(false);
        onMenuClose?.();
      }
    };

    if (showMenu || showReactions) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("touchstart", handleClickOutside);
      };
    }
  }, [showMenu, showReactions, onMenuClose]);

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
    setShowReactions(false);
  };

  const handleReactionClick = (e) => {
    e.stopPropagation();
    setShowReactions(!showReactions);
    setShowMenu(false);
  };

  const handleReply = (e) => {
    e.stopPropagation();
    onReply(message);
    setShowMenu(false);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit(message);
    setShowMenu(false);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    onDelete(message._id);
    setShowDeleteModal(false);
  };

  const handleReport = (e) => {
    e.stopPropagation();
    onReport(message._id);
    setShowMenu(false);
  };

  const handleEmojiClick = (emoji, e) => {
    e.stopPropagation();
    onReact(message._id, emoji);
    setShowReactions(false);
  };

  // Check if message can be edited (within 15 minutes)
  const canEdit = () => {
    if (!isMyMessage || message.isDeleted || !message.content) return false;
    const messageTime = new Date(message.createdAt).getTime();
    const now = Date.now();
    const fifteenMinutes = 15 * 60 * 1000;
    return now - messageTime < fifteenMinutes;
  };

  // Check if message can be deleted (within 24 hours)
  const canDelete = () => {
    if (!isMyMessage || message.isDeleted || !message.content) return false;
    const messageTime = new Date(message.createdAt).getTime();
    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000;
    return now - messageTime < twentyFourHours;
  };

  const isDisabled = message.isDeleted || !message.content;

  return (
    <>
      <div className="msgx-menu-container" ref={menuRef}>
        {/* Reaction Button */}
        <motion.button
          className={`msgx-menu-action-btn ${isDisabled ? "disabled" : ""}`}
          onClick={handleReactionClick}
          title="React"
          disabled={isDisabled}
          whileHover={!isDisabled ? { scale: 1.1 } : {}}
          whileTap={!isDisabled ? { scale: 0.95 } : {}}
        >
          <FiSmile />
        </motion.button>

        {/* Menu Button */}
        <motion.button
          className="msgx-menu-action-btn msgx-menu-btn"
          onClick={handleMenuClick}
          title="More options"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <FiMoreVertical />
        </motion.button>

        {/* Reaction Picker */}
        <AnimatePresence>
          {showReactions && (
            <motion.div
              className={`msgx-reaction-picker ${
                isMyMessage ? "sent" : "received"
              }`}
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 10 }}
              transition={{ type: "spring", damping: 25, stiffness: 400 }}
            >
              {quickEmojis.map((emoji, index) => (
                <motion.button
                  key={emoji}
                  className="msgx-reaction-emoji"
                  onClick={(e) => handleEmojiClick(emoji, e)}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.03 }}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {emoji}
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Menu Dropdown */}
        <AnimatePresence>
          {showMenu && (
            <motion.div
              ref={dropdownRef}
              className={`msgx-menu-dropdown ${
                isMyMessage ? "sent" : "received"
              } ${forceShowMenu ? "gesture-triggered" : ""}`}
              style={
                forceShowMenu
                  ? {
                      top: `${menuPosition.top}px`,
                      left: `${menuPosition.left}px`,
                    }
                  : {}
              }
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ type: "spring", damping: 25, stiffness: 400 }}
            >
              {/* Reply - Disabled for deleted messages */}
              {!message.isDeleted && message.content && (
                <motion.button
                  className="msgx-menu-item"
                  onClick={handleReply}
                  whileHover={{ backgroundColor: "var(--msgx-hover)" }}
                >
                  <FiCornerUpLeft />
                  <span>Reply</span>
                </motion.button>
              )}

              {/* Edit - Only for own messages within 15 min */}
              {isMyMessage && canEdit() && (
                <motion.button
                  className="msgx-menu-item"
                  onClick={handleEdit}
                  whileHover={{ backgroundColor: "var(--msgx-hover)" }}
                >
                  <FiEdit2 />
                  <span>Edit</span>
                </motion.button>
              )}

              {/* Delete - Only for own messages within 24 hours */}
              {isMyMessage && canDelete() && (
                <motion.button
                  className="msgx-menu-item danger"
                  onClick={handleDelete}
                  whileHover={{ backgroundColor: "rgba(239, 68, 68, 0.1)" }}
                >
                  <FiTrash2 />
                  <span>Delete</span>
                </motion.button>
              )}

              {/* Report - Only for others' messages */}
              {!isMyMessage && (
                <motion.button
                  className="msgx-menu-item danger"
                  onClick={handleReport}
                  whileHover={{ backgroundColor: "rgba(239, 68, 68, 0.1)" }}
                >
                  <FiFlag />
                  <span>Report</span>
                </motion.button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        messageContent={message.content}
      />
    </>
  );
};

export default MessageMenu;
